import type { CatalogField, FilterCategory, FilterOperator } from '../types/analytics.types';
import { useCatalogStore } from '../stores/catalogStore';

/**
 * Field configuration comes from the backend catalog (see catalogStore); the
 * helpers here read it so components can stay synchronous. Anything that
 * needs to re-render when the catalog loads should subscribe with
 * useCatalogStore instead.
 */
export type FilterFieldConfig = CatalogField;

export function getFieldConfig(category: FilterCategory, field: string): FilterFieldConfig | undefined {
  const { fields } = useCatalogStore.getState();
  return fields.find((f) => f.field === field && f.category === category) ?? fields.find((f) => f.field === field);
}

export function getAllFields(): FilterFieldConfig[] {
  return useCatalogStore.getState().fields;
}

export function categoryLabel(category: FilterCategory): string {
  const found = useCatalogStore.getState().categories.find((c) => c.key === category);
  return found?.label ?? category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: 'is greater than',
  greater_than_or_equals: 'is at least',
  less_than: 'is less than',
  less_than_or_equals: 'is at most',
  between: 'is between',
  in: 'is one of',
  not_in: 'is not one of',
  is_true: 'is true',
  is_false: 'is false',
  is_null: 'is empty',
  is_not_null: 'is not empty',
  within_last_days: 'within the last',
  more_than_days_ago: 'more than',
  before_date: 'is before',
  after_date: 'is after',
};

const CATEGORY_COLORS: Partial<Record<FilterCategory, string>> = {
  demographics: 'bg-info/10 text-info',
  attribution: 'bg-info/10 text-info',
  conditions: 'bg-destructive/10 text-destructive',
  care_plan: 'bg-accent text-primary',
  care_gaps: 'bg-warning/10 text-warning',
  medications: 'bg-accent text-primary',
  programs: 'bg-success/10 text-success',
  engagement: 'bg-accent text-primary',
  action_items: 'bg-warning/10 text-warning',
};

export function categoryColor(category: FilterCategory): string {
  return CATEGORY_COLORS[category] ?? 'bg-muted text-muted-foreground';
}
